// ── RENEWAL SEASONS, WORKING DAYS, AND THE PACE THAT FOLLOWS ─────────────────
//
// Kevin, 2026-10-03: *"add an automatic renewals per work day (M-F excluding holidays)
// needed in order to see everybody … Make fall crop renewal season start on September 1
// and run through September 30th. PRF renewal season November 1 through December 1, and
// spring crops January 5th through March 15th."*
//
// The three windows are HIS, stated verbatim and not derived. What IS derivable, and is
// recorded here because it decides which crop year a visit belongs to, is that each one
// ENDS ON RMA'S SALES CLOSING DATE for that line in New York — winter wheat Sep 30,
// PRF Dec 1, spring crops Mar 15. That is why the crop-year mapping below is not a guess:
// a season is the run-up to a sales closing, and a sales closing sells the NEXT crop year
// for fall and PRF, and the CURRENT one for spring.
//
//   fall   Sep 1 – Sep 30, calendar Y  ->  crop year Y+1   (CY2027 wheat sells Sep 2026)
//   prf    Nov 1 – Dec 1,  calendar Y  ->  crop year Y+1   (CY2027 PRF   sells Dec 2026)
//   spring Jan 5 – Mar 15, calendar Y  ->  crop year Y     (CY2027 corn  sells Mar 2027)
//
// ⚠ THE HOLIDAY SET IS NOT `marketHolidays` FROM api/_home.js, AND REUSING IT WOULD BE
// WRONG IN BOTH DIRECTIONS. That rule was measured against CME closures and answers a
// different question: it INCLUDES Good Friday, which is not a federal holiday and on which
// an insurance agency may well be working, and it OMITS Columbus Day and Veterans Day,
// which are federal holidays. Borrowing a measured-looking set that answers someone else's
// question is the failure this repo keeps recording, so this file states its own.
//
// ⚠ AND THIS SET IS A STATED CONVENTION, NOT A MEASUREMENT. Nothing in this database
// records which days Clover Ag is closed. The eleven US federal holidays are the honest
// default for an office calendar; every excluded date is RETURNED and printed, so a day
// the agency actually works — or does not — is visible and correctable rather than buried
// in an arithmetic.

"use strict";

// ── the three seasons, exactly as Kevin stated them ──────────────────────────
const SEASONS = Object.freeze({
  fall: Object.freeze({
    key: "fall", label: "Fall crops", short: "Fall",
    start: [9, 1], end: [9, 30],
    cropYearOffset: 1,
    closes: "RMA sales closing for winter wheat, Sep 30",
  }),
  prf: Object.freeze({
    key: "prf", label: "PRF", short: "PRF",
    start: [11, 1], end: [12, 1],
    cropYearOffset: 1,
    closes: "RMA sales closing for PRF, Dec 1",
  }),
  spring: Object.freeze({
    key: "spring", label: "Spring crops", short: "Spring",
    start: [1, 5], end: [3, 15],
    cropYearOffset: 0,
    closes: "RMA sales closing for spring crops, Mar 15",
  }),
});
const SEASON_KEYS = Object.freeze(["fall", "prf", "spring"]);

// ── dates, in UTC throughout ─────────────────────────────────────────────────
// Every date here is a calendar DAY, never an instant. Building them in local time makes
// the answer depend on the reader's timezone, which for a date one side of midnight is a
// different working day and a different season.
const utc = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
const iso = (dt) => dt.toISOString().slice(0, 10);
const parseIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? utc(+m[1], +m[2], +m[3]) : null;
};
const addDays = (dt, n) => new Date(dt.getTime() + n * 86400000);

function nthWeekday(y, month, weekday, n) {       // month 1-based, weekday 0=Sun
  const first = utc(y, month, 1);
  const shift = (weekday - first.getUTCDay() + 7) % 7;
  return utc(y, month, 1 + shift + (n - 1) * 7);
}
function lastWeekday(y, month, weekday) {
  const last = new Date(Date.UTC(y, month, 0));   // day 0 of next month
  return utc(y, month, last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7));
}
// A federal holiday falling on Saturday is observed the Friday before, on Sunday the
// Monday after. That is 5 U.S.C. 6103(b) and it applies to every fixed-date holiday here —
// unlike the CME set, where a Saturday Independence Day closes the Friday but a Saturday
// New Year's Day closes nothing. Different institutions, different rules; this one is the
// office rule.
function observed(y, month, day, out, name) {
  const d = utc(y, month, day);
  const w = d.getUTCDay();
  if (w === 6) out.push({ date: iso(addDays(d, -1)), name: name + " (observed)" });
  else if (w === 0) out.push({ date: iso(addDays(d, 1)), name: name + " (observed)" });
  else out.push({ date: iso(d), name });
}

/** The eleven US federal holidays for a calendar year, each with its name. */
function federalHolidays(year) {
  const out = [];
  observed(year, 1, 1, out, "New Year's Day");
  out.push({ date: iso(nthWeekday(year, 1, 1, 3)), name: "Martin Luther King Jr. Day" });
  out.push({ date: iso(nthWeekday(year, 2, 1, 3)), name: "Washington's Birthday" });
  out.push({ date: iso(lastWeekday(year, 5, 1)), name: "Memorial Day" });
  // Juneteenth became a federal holiday in June 2021 (Public Law 117-17). A year before
  // that has ten, and saying so is cheaper than a reader wondering why a 2019 count differs.
  if (year >= 2021) observed(year, 6, 19, out, "Juneteenth");
  observed(year, 7, 4, out, "Independence Day");
  out.push({ date: iso(nthWeekday(year, 9, 1, 1)), name: "Labor Day" });
  out.push({ date: iso(nthWeekday(year, 10, 1, 2)), name: "Columbus Day" });
  observed(year, 11, 11, out, "Veterans Day");
  out.push({ date: iso(nthWeekday(year, 11, 4, 4)), name: "Thanksgiving Day" });
  observed(year, 12, 25, out, "Christmas Day");
  out.sort((a, b) => (a.date < b.date ? -1 : 1));
  return out;
}

/**
 * The working days in [startIso, endIso] INCLUSIVE — Monday to Friday, minus federal
 * holidays. Returns the days themselves and, separately, every date it excluded and why,
 * so a reader can check the count rather than take it.
 */
function workingDays(startIso, endIso) {
  const a = parseIso(startIso), b = parseIso(endIso);
  if (!a || !b || b < a) return { days: [], count: 0, holidays: [], weekendDays: 0, why: "the range is empty or inverted" };
  const hol = new Map();
  for (let y = a.getUTCFullYear(); y <= b.getUTCFullYear(); y++) {
    for (const h of federalHolidays(y)) hol.set(h.date, h.name);
  }
  const days = [], excluded = [];
  let weekend = 0;
  for (let d = a; d <= b; d = addDays(d, 1)) {
    const s = iso(d), w = d.getUTCDay();
    if (w === 0 || w === 6) { weekend++; continue; }
    if (hol.has(s)) { excluded.push({ date: s, name: hol.get(s) }); continue; }
    days.push(s);
  }
  return { days, count: days.length, holidays: excluded, weekendDays: weekend, why: null };
}

/** Which crop year a season in a given calendar year is selling. */
function cropYearFor(seasonKey, calendarYear) {
  const s = SEASONS[seasonKey];
  if (!s || !Number.isFinite(calendarYear)) return null;
  return calendarYear + s.cropYearOffset;
}

/** The window for a season in a calendar year, as ISO dates. */
function seasonWindow(seasonKey, calendarYear) {
  const s = SEASONS[seasonKey];
  if (!s || !Number.isFinite(calendarYear)) return null;
  return {
    key: s.key, label: s.label, closes: s.closes,
    start: iso(utc(calendarYear, s.start[0], s.start[1])),
    end: iso(utc(calendarYear, s.end[0], s.end[1])),
    cropYear: calendarYear + s.cropYearOffset,
    calendarYear,
  };
}

/**
 * The season a date falls in, or null. A date outside all three is OUTSIDE — this returns
 * no "nearest" season, because a pace computed against a window that has not opened is a
 * deadline nobody set.
 */
function seasonOn(isoDate) {
  const d = parseIso(isoDate);
  if (!d) return null;
  const y = d.getUTCFullYear();
  for (const k of SEASON_KEYS) {
    const w = seasonWindow(k, y);
    if (isoDate >= w.start && isoDate <= w.end) return w;
  }
  return null;
}

/** The next season to open on or after a date, so a page can name what is coming. */
function nextSeason(isoDate) {
  const d = parseIso(isoDate);
  if (!d) return null;
  const y = d.getUTCFullYear();
  let best = null;
  for (const yy of [y, y + 1]) {
    for (const k of SEASON_KEYS) {
      const w = seasonWindow(k, yy);
      if (w.start >= isoDate && (!best || w.start < best.start)) best = w;
    }
  }
  return best;
}

/**
 * How many farms a day are left to see.
 *
 * ⚠ IT COUNTS THE DAYS REMAINING, NOT THE DAYS IN THE SEASON. A pace figure computed off
 * the whole window is the pace somebody needed on day one; what a reader wants on the 14th
 * is how many a day it takes from HERE. Both are returned, because the gap between them is
 * the thing that says whether the season is slipping.
 *
 * ⚠ AND TODAY COUNTS AS A WORKING DAY IF IT IS ONE. Excluding it quietly adds a day of
 * slack to every figure on the page.
 */
function renewalPace({ total, visited, today, seasonKey, calendarYear }) {
  // ⚠ `Number(null)` IS 0, AND 0 IS FINITE. This repo records that trap in four other
  // places and this module walked straight into it: with a bare Number.isFinite the
  // unknown-customer-count refusal below was unreachable, and a book whose size we have
  // not established would have reported "0 customers, 0 a day, season complete". The
  // null/empty test has to come FIRST. Found by the checker on its first run.
  const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  const tot = n(total), vis = n(visited) || 0;
  const w = seasonKey && Number.isFinite(calendarYear) ? seasonWindow(seasonKey, calendarYear) : seasonOn(today);
  if (!w) return { ok: false, why: "no renewal season covers this date", season: null };
  if (tot == null) return { ok: false, why: "the customer count is not established", season: w };

  const whole = workingDays(w.start, w.end);
  // Remaining starts at today where today is inside the window, at the start where the
  // season has not opened, and is empty once it has closed.
  const from = today > w.end ? null : (today < w.start ? w.start : today);
  const left = from ? workingDays(from, w.end) : { days: [], count: 0, holidays: [], weekendDays: 0 };
  const remaining = Math.max(0, tot - vis);

  return {
    ok: true,
    season: w,
    total: tot,
    visited: vis,
    remaining,
    workingDaysInSeason: whole.count,
    workingDaysLeft: left.count,
    holidaysInSeason: whole.holidays,
    // The pace the season demanded on day one, and the pace it demands now. Both to two
    // decimals: a renewal is a whole visit, and rounding 2.04 to 2 hides a farm a week.
    paceAtStart: whole.count > 0 ? +(tot / whole.count).toFixed(2) : null,
    paceNow: left.count > 0 ? +(remaining / left.count).toFixed(2) : null,
    // ⚠ NO WORKING DAYS LEFT AND FARMS STILL TO SEE IS NOT A PACE OF INFINITY. It is a
    // season that cannot be finished at any pace, which is a different sentence.
    unreachable: left.count === 0 && remaining > 0,
    closed: today > w.end,
    notYetOpen: today < w.start,
  };
}

/**
 * The working days a plan would actually be driven on, starting from `from`.
 *
 * Kevin, 2026-10-03: *"Plan optimal customer groups so I can plan out a schedule."* A list of
 * "Day 1, Day 2, Day 3" is a grouping; a schedule is dates. This maps one onto the other.
 *
 * ⚠ IT RETURNS FEWER DATES THAN ASKED FOR RATHER THAN RUNNING PAST THE SEASON. A plan needing
 * 44 days in a 21-day window is the finding, and inventing October dates for a season that
 * closes on September 30 would hide it behind a calendar that looks complete. The shortfall
 * is returned as a count so the caller can say so.
 *
 * ⚠ AND IT STARTS NO EARLIER THAN THE SEASON OPENS. Planning from today when today is in
 * August would put renewal visits before the window Kevin set.
 */
function scheduleDays({ seasonKey, calendarYear, count, from = null }) {
  const w = seasonWindow(seasonKey, calendarYear);
  const n = Math.max(0, Math.round(Number(count) || 0));
  if (!w) return { dates: [], shortBy: n, why: "no such season" };
  const start = !from || from < w.start ? w.start : from;
  if (start > w.end) return { dates: [], shortBy: n, why: "the season has closed", season: w };
  const avail = workingDays(start, w.end).days;
  return {
    dates: avail.slice(0, n),
    // What could not be fitted. Named rather than silently truncated.
    shortBy: Math.max(0, n - avail.length),
    availableDays: avail.length,
    season: w,
    why: null,
  };
}

module.exports = {
  SEASONS, SEASON_KEYS,
  federalHolidays, workingDays,
  seasonWindow, seasonOn, nextSeason, cropYearFor,
  renewalPace, scheduleDays,
};
